import { CanvasElement } from "shared/datamodel/schemas";
import { useEffect, useState } from "react";
import { ElementRule, ElementRuleComparator } from "shared/datamodel/schemas/element-data-layer";
import { changedPropertiesForElement } from "../editor-ui/element-rules-editor/rule-cell/rule-creator-utils";
import { getElementTypeForId } from "../../elements/canvas-elements-utils";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import { useDynamicRules } from "./use-dynamic-rules";
import { useDynamicIntegrations } from "./use-dynamic-integrations";

export function useElementWithRulesProcessing<T extends CanvasElement>({ id, element }: { id: string; element: T }): T {
  const [changedProperties, setChangedProperties] = useState<Record<string, any>>({});
  const { activeRules } = useDynamicRules(id, element);
  const { getFieldValue } = useDynamicIntegrations(id, element);
  const elementType = getElementTypeForId(id);

  const memoedElement = useDeepEqualMemo(element);
  useEffect(() => {
    const propsRules = activeRules.filter((rule) => rule.action.property !== "icon");
    const passingRules = getPassingRules(propsRules, getFieldValue);
    const toChange = passingRules.reduce((acc, rule) => {
      const changed = changedPropertiesForElement(elementType, rule.action.property, rule.action.value);
      return { ...acc, ...changed };
    }, {} as Record<string, any>);

    if (
      propsRules.some((rule) => rule.action.property === "scale") &&
      (toChange.scaleX === undefined || toChange.scaleY === undefined)
    ) {
      toChange.scaleX = 1;
      toChange.scaleY = 1;
    }

    setChangedProperties(toChange);
  }, [memoedElement, getFieldValue, activeRules, elementType]);

  if (Object.keys(changedProperties).length === 0) {
    return memoedElement;
  }

  return {
    ...memoedElement,
    ...changedProperties,
  };
}

function passesComparator(value: string, compareValue: string, comparator: ElementRuleComparator): boolean {
  const numericValue = value.endsWith("%") ? Number.parseFloat(value.slice(0, -1)) : Number.parseFloat(value);
  const numericCompareValue = compareValue.includes("%")
    ? Number.parseFloat(compareValue.slice(0, -1))
    : Number.parseFloat(compareValue);
  switch (comparator) {
    case "eq": {
      return value == compareValue;
    }
    case "neq": {
      return value != compareValue;
    }
    case "contains": {
      return compareValue !== "" && value.includes(compareValue);
    }
    case "not_contains": {
      return compareValue !== "" && !value.includes(compareValue);
    }
    case "gt": {
      return numericValue > numericCompareValue;
    }
    case "lt": {
      return numericValue < numericCompareValue;
    }
    case "gte": {
      return numericValue >= numericCompareValue;
    }
    case "lte": {
      return numericValue <= numericCompareValue;
    }
    default: {
      return false;
    }
  }
}

function getPassingRules(
  rules: ElementRule[],
  getValueFunction: (key: string, integrationId?: string, itemId?: string, columnId?: string) => string | undefined
): ElementRule[] {
  const passingRules: ElementRule[] = [];

  for (const rule of rules) {
    const [key, integrationId, itemId, columnId] = rule.fieldId.split(":");
    const value = getValueFunction(key, integrationId, itemId, columnId);
    if (!value) continue;

    const compareValue = rule.compareValue;
    const comparator = rule.comparator;

    if (passesComparator(value.toLowerCase(), compareValue.toLowerCase(), comparator)) {
      passingRules.push(rule);
    }
  }

  return passingRules;
}
